import { useMemo } from "react";

import { useConfiguration } from "~/contexts/configuration";
import { StorageFactory } from "~/services/storage.factory";
import { CacheKeys } from "~/shared/cache-keys";

type GuestCacheKey = CacheKeys.ANONYMOUS_APP_TOKEN | CacheKeys.ANONYMOUS_ORDER_COMPLETED | CacheKeys.ANONYMOUS_ORDER_ID;

/** Owns guest storage scoping and cross-tab event filtering. Stores one scoped string per shared key. */
export function useGuestCartStorage() {
  const { organizationUnitId } = useConfiguration();

  return useMemo(() => {
    const storage = new StorageFactory("localStorage");
    const prefix = `${organizationUnitId}:`;
    const decode = (value: string | null) =>
      organizationUnitId && value?.startsWith(prefix) ? value.slice(prefix.length) : null;

    return {
      get: (name: GuestCacheKey) => decode(storage.get(name)),
      set: (name: GuestCacheKey, value: string) => {
        if (organizationUnitId) storage.set(name, `${prefix}${value}`);
      },
      remove: (name: GuestCacheKey) => {
        if (decode(storage.get(name)) !== null) storage.remove(name);
      },
      subscribe: (name: GuestCacheKey, listener: (event: { newValue: string | null }) => void) => {
        if (!organizationUnitId || typeof window === "undefined") return () => {};

        const onStorage = (event: StorageEvent) => {
          if (event.storageArea !== window.localStorage || event.key !== name) return;
          const newValue = decode(event.newValue);
          // A foreign overwrite is ignored; a deletion belongs to the old value's country.
          if (newValue !== null || (event.newValue === null && decode(event.oldValue) !== null)) {
            listener({ newValue });
          }
        };
        window.addEventListener("storage", onStorage);
        return () => window.removeEventListener("storage", onStorage);
      },
    };
  }, [organizationUnitId]);
}
